import type {CSSProperties} from 'react';
import {WEDGE_RECENTS} from '@territory/tokens';
import {radius, space, weight} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeRecentsLook = 'sort' | 'sort-on' | 'query' | 'row' | 'empty';

/* Released Live and Recent screen (prep-screens-5 RecentsScreen), off-scale. The sort chip is a pill with 4px by 10px padding, 11.5px
   bold type and no border; idle it takes the 5% black veil with the secondary ink, on it takes the accent fill with the fixed white
   ink. The query chip is a pill with a hairline separator border, 8px by 13px padding and 12.5px semibold type in the Wedge font. The row
   is a full-width flex card: surface fill, card border, 14px radius, 13px by 14px padding, 12px gap. The empty action is a full-width
   dashed block with 26px by 16px padding. Only the query chip names the Wedge font; the others retain the shipped sheet's button font,
   as the source does. The caller owns the margins and the children. */
const SORT_FONT = 11.5, SORT_PY = 4, SORT_PX = 10;
const QUERY_FONT = 12.5, QUERY_PY = 8, QUERY_PX = 13, QUERY_GAP = 6;
const ROW_PY = 13, ROW_PX = 14;
const EMPTY_PY = 26, EMPTY_PX = 16;

export function wedgeRecentsStyle(look: WedgeRecentsLook): CSSProperties {
  if (look === 'sort' || look === 'sort-on') {
    const on = look === 'sort-on';
    return {
      fontSize: SORT_FONT, fontWeight: weight.bold, padding: `${SORT_PY}px ${SORT_PX}px`, borderRadius: radius.pill,
      border: 'none', cursor: 'pointer',
      background: on ? wedgeExactRole('accent') : WEDGE_RECENTS.sortFill,
      color: on ? WEDGE_RECENTS.sortInk : wedgeExactRole('labelSec'),
    };
  }
  if (look === 'query') return {
    flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: QUERY_GAP,
    background: wedgeExactRole('surface'), border: `1px solid ${wedgeExactRole('separator')}`,
    borderRadius: radius.pill, padding: `${QUERY_PY}px ${QUERY_PX}px`,
    fontSize: QUERY_FONT, fontWeight: weight.semibold, color: wedgeExactRole('labelSec'),
    fontFamily: wedgeExactRole('font'), cursor: 'pointer', whiteSpace: 'nowrap',
    textAlign: 'left', WebkitTapHighlightColor: 'transparent',
  };
  if (look === 'row') return {
    display: 'flex', alignItems: 'center', gap: space[3], padding: `${ROW_PY}px ${ROW_PX}px`,
    background: wedgeExactRole('surface'), border: `1px solid ${wedgeExactRole('cardBorder')}`,
    borderRadius: radius.xl, cursor: 'pointer', textAlign: 'left', width: '100%',
    WebkitTapHighlightColor: 'transparent',
  };
  return {
    width: '100%', padding: `${EMPTY_PY}px ${EMPTY_PX}px`, borderRadius: radius.xl,
    border: `1px dashed ${wedgeExactRole('separator')}`, background: 'transparent', cursor: 'pointer',
    color: wedgeExactRole('labelSec'),
  };
}
